import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { applyToPartnerProgram } from "@/lib/partners.functions";

const TYPES = [
  "Advisor or fractional CFO",
  "Accountant",
  "Lawyer",
  "Broker",
  "Consultant",
  "Software or data room vendor",
  "Accelerator or angel group",
  "Media or newsletter",
  "Customer referring a peer",
  "Other",
];

export const Route = createFileRoute("/partners_/apply")({
  head: () => ({
    meta: [
      { title: "Apply to the CapitalDart Partner Program" },
      {
        name: "description",
        content:
          "Apply to refer CapitalDart. Open to advisors, accountants, lawyers, brokers, consultants, vendors, accelerators, newsletters and customers referring peers.",
      },
      { property: "og:title", content: "Apply to the CapitalDart Partner Program" },
      {
        property: "og:description",
        content:
          "Apply to refer CapitalDart. Earn a share of software revenue on every purchase you send, by link or by naming the company you spoke to.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: PartnerApplyPage,
});

function PartnerApplyPage() {
  const [email, setEmail] = React.useState("");
  const [contactName, setContactName] = React.useState("");
  const [companyName, setCompanyName] = React.useState("");
  const [website, setWebsite] = React.useState("");
  const [partnerType, setPartnerType] = React.useState(TYPES[0]);
  const [audience, setAudience] = React.useState("");

  const apply = useMutation({
    mutationFn: () =>
      applyToPartnerProgram({
        data: {
          email: email.trim(),
          contactName: contactName.trim(),
          companyName: companyName.trim() || null,
          website: website.trim() || null,
          partnerType,
          audience: audience.trim() || null,
        },
      }),
  });

  const result = apply.data;

  return (
    <main className="mx-auto max-w-[720px] px-4 pb-20 pt-10">
      <Link
        to="/partners"
        className="text-[12.5px] text-brand-muted no-underline hover:text-brand-ink"
      >
        ← Back to the partner program
      </Link>
      <h1 className="mt-3 font-serif text-[34px] font-normal leading-tight tracking-tight text-brand-ink">
        Apply to partner.
      </h1>
      <p className="mt-2 max-w-[60ch] text-[15px] leading-relaxed text-brand-muted">
        Tell us who you are and who you work with. We review every
        application by hand and reply by email. Approved partners get a
        dashboard with a referral link, a named referral form and a resource
        hub.
      </p>

      {result?.ok ? (
        <div className="mt-8 rounded-2xl border border-brand-line bg-brand-paper p-6">
          <h2 className="font-serif text-[22px] text-brand-ink">
            {result.duplicate ? "You have already applied." : "Application received."}
          </h2>
          <p className="mt-2 text-[14.5px] leading-relaxed text-brand-muted">
            {result.duplicate
              ? `We already hold an application for that address. Its status is ${result.status}.`
              : "We will review it and reply by email. Once approved, sign in with this same address and your partner dashboard appears at /partners/dashboard."}
          </p>
        </div>
      ) : (
        <form
          className="mt-8 space-y-4 rounded-2xl border border-brand-line bg-brand-paper p-6"
          onSubmit={(e) => {
            e.preventDefault();
            apply.mutate();
          }}
        >
          <div>
            <Label htmlFor="pa-name">Your name</Label>
            <Input
              id="pa-name"
              required
              maxLength={200}
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="pa-email">Email</Label>
            <Input
              id="pa-email"
              type="email"
              required
              maxLength={320}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <p className="mt-1 text-[11.5px] text-brand-muted">
              Use the address you will sign in with. Your dashboard is tied to it.
            </p>
          </div>
          <div>
            <Label htmlFor="pa-company">Company (optional)</Label>
            <Input
              id="pa-company"
              maxLength={200}
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="pa-website">Website or profile (optional)</Label>
            <Input
              id="pa-website"
              maxLength={300}
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://"
            />
          </div>
          <div>
            <Label htmlFor="pa-type">What kind of partner are you?</Label>
            <select
              id="pa-type"
              className="mt-1 w-full rounded-md border border-brand-line bg-white px-3 py-2 text-[14px] text-brand-ink"
              value={partnerType}
              onChange={(e) => setPartnerType(e.target.value)}
            >
              {TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="pa-audience">Who do you work with?</Label>
            <Textarea
              id="pa-audience"
              maxLength={2000}
              rows={4}
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              placeholder="A sentence or two on your clients, readers or members, and how you would recommend CapitalDart."
            />
          </div>
          <p className="text-[12px] leading-relaxed text-brand-muted">
            Compensation is a share of CapitalDart software revenue only. It is
            never tied to whether a raise closes or how large it is. A partner
            never solicits investors and never makes representations about any
            offering. Applying means you accept the{" "}
            <Link to="/affiliate-terms" className="underline">
              partner terms
            </Link>
            .
          </p>
          <Button type="submit" disabled={apply.isPending}>
            {apply.isPending ? "Sending…" : "Send application"}
          </Button>
          {apply.error && (
            <p className="text-[12.5px] text-destructive">
              Something went wrong. Try again, or email us.
            </p>
          )}
        </form>
      )}
    </main>
  );
}
